import { CommonModule } from '@angular/common';
import { Component, Output, EventEmitter, Input, HostListener, OnInit, AfterViewInit } from '@angular/core';
import { FormsModule } from "@angular/forms";
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { InputNumberModule } from 'primeng/inputnumber';

@Component({
  selector: 'app-discount-input',
  imports: [
    CommonModule,
    FormsModule,
    InputTextModule,
    ButtonModule,
    InputNumberModule
  ],
  templateUrl: './discount-input.component.html'
})
export class DiscountInputComponent implements OnInit, AfterViewInit {
  @Input() grossTotal: number = 0;
  @Output() discountConfirmed = new EventEmitter<number>();
  @Output() discountCancelled = new EventEmitter<void>();

  discountValue: number | null = null; // Valor monetário
  discountPercent: number | null = null; // Valor percentual

  ngOnInit(): void {
    // Focar no primeiro input quando o componente for inicializado
    this.focusFirstInput();
  }

  ngAfterViewInit(): void {
    // Garantir que o foco seja aplicado após a view ser inicializada
    setTimeout(() => {
      this.focusFirstInput();
    }, 100);
  }

  @HostListener('window:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent): void {
    // Capturar Enter globalmente para confirmar desconto
    if (event.key === 'Enter' || event.keyCode === 13) {
      const target = event.target as HTMLElement;
      
      // Verificar se o evento está vindo de dentro do componente de desconto
      const isInDiscountComponent = target && (
        target.closest('app-discount-input') !== null ||
        target.tagName === 'INPUT' ||
        target.classList.contains('p-inputnumber-input') ||
        target.closest('.p-inputnumber') !== null ||
        target.tagName === 'BUTTON'
      );
      
      if (isInDiscountComponent) {
        event.preventDefault();
        event.stopPropagation();
        this.confirmDiscount();
      }
    }
  }

  onDiscountValueChange(): void {
    // Se o valor monetário foi preenchido, limpar o percentual
    if (this.discountValue !== null && this.discountValue > 0) {
      this.discountPercent = null;
    }
  }

  onDiscountPercentChange(): void {
    // Se o percentual foi preenchido, limpar o valor monetário
    if (this.discountPercent !== null && this.discountPercent > 0) {
      this.discountValue = null;
    }
  }

  confirmDiscount(): void {
    let discountAmount = 0;

    if (this.discountValue !== null && this.discountValue >= 0) {
      // Usar valor monetário (permite 0 para remover desconto)
      discountAmount = this.discountValue;
    } else if (this.discountPercent !== null && this.discountPercent >= 0) {
      // Calcular desconto percentual sobre o total bruto (permite 0 para remover desconto)
      discountAmount = (this.grossTotal * this.discountPercent) / 100;
    }

    // Validar que o desconto não seja maior que o total bruto
    if (discountAmount > this.grossTotal) {
      discountAmount = this.grossTotal;
    }

    // Permitir desconto 0 (remover desconto) ou qualquer valor positivo
    this.discountConfirmed.emit(discountAmount);
  }

  clear(): void {
    this.discountValue = null;
    this.discountPercent = null;
  }

  /**
   * Foca no primeiro input (desconto em valor)
   */
  private focusFirstInput(): void {
    const tryFocus = (attempt: number = 0) => {
      // Tentar encontrar o input pelo ID
      let discountInput = document.getElementById('discount-value-input');
      
      // Se não encontrar pelo ID, tentar encontrar o input dentro do p-inputNumber
      if (!discountInput) {
        const pInputNumberContainer = document.querySelector('[id="discount-value-input"]')?.closest('.p-inputnumber');
        if (pInputNumberContainer) {
          discountInput = pInputNumberContainer.querySelector('input') as HTMLInputElement;
        }
      }
      
      // Se ainda não encontrou, tentar buscar por qualquer input dentro do container
      if (!discountInput) {
        const container = document.querySelector('[id="discount-value-input"]')?.parentElement;
        if (container) {
          discountInput = container.querySelector('input') as HTMLInputElement;
        }
      }
      
      // Tentar buscar pelo seletor do PrimeNG
      if (!discountInput) {
        const pInputNumberInput = document.querySelector('#discount-value-input .p-inputnumber-input') as HTMLInputElement;
        if (pInputNumberInput) {
          discountInput = pInputNumberInput;
        }
      }
      
      if (discountInput && discountInput instanceof HTMLInputElement) {
        // Verificar se o input está visível antes de focar
        const isVisible = discountInput.offsetParent !== null;
        if (isVisible) {
          discountInput.focus();
          discountInput.select();
          return true;
        }
      }
      
      // Se não conseguiu focar e ainda há tentativas, tentar novamente
      if (attempt < 5) {
        setTimeout(() => {
          tryFocus(attempt + 1);
        }, 100 * (attempt + 1)); // Delay crescente: 100ms, 200ms, 300ms, etc.
      }
      return false;
    };
    
    // Começar a tentar após um pequeno delay inicial
    setTimeout(() => {
      tryFocus(0);
    }, 50);
  }
}

